import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SectionHeader from "../../../../Components/SectionHeader";
import { openingRoles } from "../../../../OpeningRolesData";
import { MapPin } from "lucide-react";
import GradientGlowDivider from "../../../../Components/GradientGlowDivider";
import WhiteButton from "../../../../Components/WhiteButton";

const rolesList = [
  {
    title: "Collaborate Across Industries",
    description:
      "Build solutions for diverse sectors, with clients who are shaping tomorrow.",
  },
  {
    title: "Learn Beyond Boundaries",
    description:
      "Join a culture where people learn from each other and push boundaries together.",
  },
  {
    title: "Mentorship with Depth",
    description:
      "Get guided by experts who can helps you grow both as a thinker and a builder.",
  },
];

const CareersRoleSection = () => {
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleExpand = (index) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <section
      id="see-open-roles"
      className="relative p-4 md:p-20 mt-[4%] mb-[28%] md:my-[4%]"
    >
      <SectionHeader
        childClassName="md:px-[4%] text-base md:text-lg"
        title={
          <div>
            Shape the Future,
            <br /> Not Just Your Role
          </div>
        }
        description="We don’t just offer jobs, We craft meaningful journeys in AI"
      />
      <div
        className={`group relative p-[0.5px] bg-banner-border rounded-2xl mx-4 md:mx-5 `}
      >
        <div className="flex-1 flex flex-col md:flex-row relative bg-app-background rounded-2xl ps-6 pr-4 md:px-12 pt-10 md:pt-8 pb-14 md:pb-16 space-y-7 md:space-y-0 md:space-x-14">
          {rolesList.map((role, index) => (
            <div
              key={role.title}
              className="flex flex-col items-start text-start"
            >
              <h2 className="font-extrabold text-3xl md:text-4xl leading-[140%] tracking-[0%]">
                {String(index + 1).padStart(2, "0")}
              </h2>
              <h3 className="font-bold text-xl md:text-2xl leading-[140%] tracking-[0%] text-start pt-2 md:pt-4">
                {role.title}
              </h3>
              <p className="font-normal text-sm md:text-lg leading-[140%] tracking-[0%] pt-0.5">
                {role.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="py-[30%] md:py-[10%] flex flex-col items-center justify-center">
        <h2 className="font-extrabold text-[32px] md:text-[48px] leading-[140%] tracking-[0%] text-center mb-8 md:mb-12">
          Want to be a part of Datapixl?
        </h2>

        <div className="w-full md:w-[60%] flex flex-col items-center justify-center space-y-8">
          {openingRoles.map((job, index) => {
            if (job.generalRole) return null;
            const isExpanded = expandedItems.has(index);
            return (
              <div
                key={job.id}
                id={`job-${job.id}`}
                className="w-full transition-all duration-500 ease-in-out group relative p-[0.5px] rounded-2xl"
              >
                <div
                  className={`flex flex-col p-4 md:px-8 md:py-6 text-white no-underline relative bg-app-background ${
                    isExpanded ? "" : "md:hover:bg-[#141414]"
                  } rounded-2xl`}
                >
                  {/* Title + Button Row */}
                  <div
                    onClick={() => toggleExpand(index)}
                    className="flex items-center cursor-pointer select-none w-full"
                  >
                    {/* Title */}
                    <span className="flex-1 pr-4 font-bold text-[24px] md:text-[40px] leading-[140%] tracking-[0%] line-clamp-1">
                      {job.title}
                    </span>

                    {/* Expand/Collapse Button with rotation */}
                    <button
                      style={
                        isExpanded
                          ? { boxShadow: "0px 0px 5.6px 0px #A8A8A8" }
                          : {}
                      }
                      aria-label="Expand or collapse"
                      className={`flex-shrink-0 w-9 md:w-14 h-9 md:h-14 flex items-center justify-center rounded-full border-white border-[1px] transition-transform duration-300 ease-in-out z-10 ${
                        isExpanded ? "bg-black rotate-0" : "rotate-180"
                      }`}
                    >
                      <img
                        src="/icons/Expand.svg"
                        alt="Close"
                        className="h-5 w-5 md:h-8 md:w-8"
                      />
                    </button>
                  </div>

                  {/* Location */}
                  <div className="flex flex-row items-center justify-start space-x-1 md:space-x-2 mt-2 md:mt-3 transition-all duration-300 ease-out">
                    <MapPin
                      className={`${
                        isExpanded ? "text-white" : "text-[#7D7D7D]"
                      } h-3.5 w-3.5 md:h-6 md:w-6`}
                    />
                    <span
                      className={`font-medium text-[12px] md:text-[22px] leading-[140%] tracking-[0%] ${
                        isExpanded ? "text-white" : "text-[#7D7D7D]"
                      } mr-4 md:mr-12`}
                    >
                      {job.location}
                    </span>
                  </div>

                  {/* Expanded Content with smooth animation */}
                  <div
                    className={`grid overflow-hidden transition-all duration-500 ease-in-out ${
                      isExpanded
                        ? "grid-rows-[1fr] opacity-100 mt-2"
                        : "grid-rows-[0fr] opacity-0 mt-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <GradientGlowDivider className="my-4 md:my-7" />

                      <h2 className="jobDescTitle">About the Role</h2>
                      <p className="jobDescSubtitle pt-2">{job.aboutRole}</p>

                      <h2 className="jobDescTitle jobDescTitlePadding">
                        Responsibilities
                      </h2>
                      <ul className="careersBulletList jobDescSubtitle pt-2">
                        {job.responsibilities.map((point, index) => (
                          <li key={index}>{point}</li>
                        ))}
                      </ul>

                      <h2 className="jobDescTitle jobDescTitlePadding">
                        What We’re Looking For
                      </h2>
                      <ul className="careersBulletList jobDescSubtitle pt-2">
                        {job.lookingFor.map((point, index) => (
                          <li key={index}>{point}</li>
                        ))}
                      </ul>

                      <div className="pt-10 md:pt-12 pb-12 md:pb-14 flex">
                        <WhiteButton
                          text="Apply for this position"
                          onClick={() => navigate(`/${job.id}/form`)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-7 md:mt-8 font-medium  md:text-xl leading-[140%] tracking-[0%] flex flex-col md:block items-center justify-center">
          Want to apply for a different role? <br className="md:hidden" />
          <Link
            to="/0/form"
            className="font-medium md:text-xl leading-[140%] tracking-[0%] underline  underline-offset-[20%] pt-2 md:pt-0"
          >
            Apply here
          </Link>
        </p>
      </div>
    </section>
  );
};

export default CareersRoleSection;
